/* eslint-disable max-lines */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  ClientKind,
  InspectionType,
  RegistrationUf,
  Status,
  StatusCode,
  ufAbbreviation,
  ufAbbreviationToEnum,
} from '../../api/enumerations';
import { AssociationWOData } from '../../api/registryAssociation/types';
import { removeRequest } from '../../api/theHive/requests';
import {
  createWorkOrder,
  editWorkOrder,
  GetWorkOrder,
} from '../../api/workOrders';
import { WorkOrderData } from '../../api/workOrders/types';
import { parcelGroup, privateUnitsGroup } from '../../constants/selectOptions';
import { GlobalContext } from '../../context/global';
import {
  isPlaceholderSelectOption,
  isProtoBank,
  validateFiles,
} from '../../helpers';
import {
  AssociateProps,
  useAssociateWorkOrder,
} from '../../hooks/useAssociateWorkOrder';
import { useAttachment } from '../../hooks/useAttachmentFiles';
import useErrorMessage from '../../hooks/useErrorMessage';
import useGeneral from '../../hooks/useGeneral';
import useSearchCep from '../../hooks/useSearchCep';

type LatLngLiteral = google.maps.LatLngLiteral;
interface ICompleteSections {
  reportData: boolean;
  propertyData: boolean;
  propertyAddress: boolean;
  scheduleResponsibleData: boolean;
  propertyDetails: boolean;
}

interface UseEntranceHook {
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  verifyCep: (value: string) => Promise<void>;
  handleFileUpload: (
    e: ChangeEvent<HTMLInputElement>,
    value: string
  ) => Promise<void>;
  handleRemoveRequest: () => Promise<void>;
  address: string;
  setAddress: (value: string) => void;
  addressNumber: string;
  setAddressNumber: (value: string) => void;
  isWithoutNumber: boolean;
  setIsWithoutNumber: (value: boolean) => void;
  builtArea: number;
  setBuiltArea: (value: number) => void;
  cep: string;
  setCep: (value: string) => void;
  cepError: boolean;
  city: string;
  setCity: (value: string) => void;
  client: string;
  setClient: (value: string) => void;
  clientType: number;
  setClientType: (value: number) => void;
  complement: string;
  setComplement: (value: string) => void;
  concept: number;
  setConcept: (value: number) => void;
  district: string;
  setDistrict: (value: string) => void;
  evaluationType: number;
  setEvaluationType: (value: number) => void;
  goal: number;
  setGoal: (value: number) => void;
  judicialDistrict: string;
  setJudicialDistrict: (value: string) => void;
  propertyFunction: number;
  setPropertyFunction: (value: number) => void;
  propertyType: number;
  setPropertyType: (value: number) => void;
  propertyUse: number;
  setPropertyUse: (value: number) => void;
  registerNumber: string;
  setRegisterNumber: (value: string) => void;
  registryOffice: string;
  setRegistryOffice: (value: string) => void;
  book: string;
  setBook: (value: string) => void;
  registrationUf: number;
  setRegistrationUf: (value: number) => void;
  setRequiresInspection: (value: boolean) => void;
  solicitor: string;
  setSolicitor: (value: string) => void;
  totalArea: number;
  setTotalArea: (value: number) => void;
  uf: number;
  setUf: (value: number) => void;
  zone: number;
  setZone: (value: number) => void;
  propertyData: WorkOrderData | undefined;
  registerFileName: string;
  iptuFileName: string;
  createdAt: string;
  downloadIptu: string;
  downloadRegister: string;
  referenceNumber: number;
  newProperty: boolean;
  searchMap: boolean;
  setSearchMap: (value: boolean) => void;
  isAddressEdited: boolean;
  setIsAddressEdited: (value: boolean) => void;
  hasSearched: boolean;
  setHasSearched: (value: boolean) => void;
  pinPlace: LatLngLiteral;
  setPinPlace: (value: LatLngLiteral) => void;
  submitLoading: boolean;
  removeRequestLoading: boolean;
  inspectionType: number;
  setInspectionType: (value: number) => void;
  responsibleName: string;
  setResponsibleName: (value: string) => void;
  emailResponsible: string;
  setEmailResponsible: (value: string) => void;
  phoneResponsible: string;
  setPhoneResponsible: (value: string) => void;
  completedSections: ICompleteSections;
  shouldAssociate: boolean | null;
  setShouldAssociate: (value: boolean | null) => void;
  setAssociateIds: (value: number[]) => void;
  associatedWorkData: AssociationWOData[];
}

const useEntrance = (): UseEntranceHook => {
  // Client Data
  const [client, setClient] = useState('');
  const [solicitor, setSolicitor] = useState('');
  const [clientType, setClientType] = useState(0);
  const [goal, setGoal] = useState(0);
  const [evaluationType, setEvaluationType] = useState(0);
  const [inspectionType, setInspectionType] = useState(0);
  const [propertyType, setPropertyType] = useState(0);
  const [propertyUse, setPropertyUse] = useState(0);

  // Property Data - files
  const [iptuFile, setIptuFile] = useState<File | string>();
  const [registerFile, setRegisterFile] = useState<File | string>();
  const [registerFileName, setRegisterFileName] = useState('');
  const [iptuFileName, setIptuFileName] = useState('');
  const [downloadIptu, setDownloadIptu] = useState('');
  const [downloadRegister, setDownloadRegister] = useState('');

  // Property Data - information
  const [judicialDistrict, setJudicialDistrict] = useState('');
  const [propertyFunction, setPropertyFunction] = useState(0);
  const [registerNumber, setRegisterNumber] = useState('');
  const [registryOffice, setRegistryOffice] = useState('');
  const [book, setBook] = useState('');
  const [registrationUf, setRegistrationUf] = useState(0);
  const [requiresInspection, setRequiresInspection] = useState(true);
  const [zone, setZone] = useState(0);
  const [concept, setConcept] = useState(0);

  // Property Address
  const [address, setAddress] = useState('');
  const [complement, setComplement] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [isWithoutNumber, setIsWithoutNumber] = useState(false);
  const [cepError, setCepError] = useState(false);
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [cep, setCep] = useState('');
  const [uf, setUf] = useState<RegistrationUf>(0);
  const [searchMap, setSearchMap] = useState(true);
  const [pinPlace, setPinPlace] = useState<LatLngLiteral>({
    lat: -23.56162,
    lng: -46.65591,
  });

  // Property Details
  const [builtArea, setBuiltArea] = useState(0);
  const [totalArea, setTotalArea] = useState(0);

  // schedule Responsible Data
  const [responsibleName, setResponsibleName] = useState('');
  const [emailResponsible, setEmailResponsible] = useState('');
  const [phoneResponsible, setPhoneResponsible] = useState('');

  const [propertyData, setPropertyData] = useState<WorkOrderData>();
  const [createdAt, setCreatedAt] = useState('');
  const [referenceNumber, setReferenceNumber] = useState(0);
  const [newProperty, setNewProperty] = useState(true);
  const [isAddressEdited, setIsAddressEdited] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [removeRequestLoading, setRemoveRequestLoading] = useState(false);
  const [shouldAssociate, setShouldAssociate] = useState<boolean | null>(null);
  const [associateIds, setAssociateIds] = useState<number[]>([]);
  const [associatedWorkData, setAssociatedWorkData] = useState<
    AssociationWOData[]
  >([]);
  const [completedSections, setCompletedSections] = useState({
    reportData: false,
    propertyData: false,
    propertyAddress: false,
    propertyDetails: false,
    scheduleResponsibleData: false,
  });

  const {
    setOpenSnackbar,
    setErrorMessage,
    setSnackbarMessage,
    isHiveOwner,
    bankCompanyData,
  } = useContext(GlobalContext);

  const { iptuLink, setIptuLink, registerLink, setRegisterLink } =
    useAttachment();
  const { getErrorMessage } = useErrorMessage();
  const { osId, checkPathname, navigateHome } = useGeneral();
  const { handleSearchCep } = useSearchCep();
  const isProtoBankCompany = isProtoBank();
  const { handleWorkOrderAssociation, getAssociatedListCallback } =
    useAssociateWorkOrder();

  const showClientKind = !isHiveOwner || isProtoBankCompany;
  const hasAssociation = associatedWorkData.length > 0;

  const getDataCallback = useCallback(async () => {
    if (checkPathname('/edit')) {
      setNewProperty(false);
      try {
        const response = await GetWorkOrder(osId);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (!response.data || response.data.status !== Status.ENTRANCE) {
          throw new Error('Algo deu errado, tente novamente.');
        }
        setPropertyData(response.data);
        setAddress(response.data.street);
        setBuiltArea(response.data.constructed_area || 0);
        setCep(response.data.zip_code);
        setCity(response.data.city);
        setClient(response.data.client_name);
        setClientType(response.data.client_kind);
        setComplement(response.data.address_complement);
        setConcept(response.data.communities_kind);
        setDistrict(response.data.district);
        setEvaluationType(response.data.evaluation_type);
        setGoal(response.data.report_goal);
        setDownloadIptu(response.data.iptu_document || '');
        setJudicialDistrict(response.data.judicial_district);
        setPropertyFunction(response.data.report_function);
        setPropertyType(response.data.real_estate_kind);
        setPropertyUse(response.data.use);
        setDownloadRegister(response.data.real_estate_registry || '');
        setRegisterNumber(response.data.registration_number);
        setRegistryOffice(response.data.registry_office ?? '');
        setBook(response.data.book ?? '');
        setRegistrationUf(response.data.uf);
        setRequiresInspection(response.data.requires_inspection);
        setTotalArea(response.data.total_area);
        setUf(ufAbbreviationToEnum[response.data.state] ?? 0);
        setZone(response.data.zone);
        setReferenceNumber(response.data.reference_number);
        setCreatedAt(response.data.created_at);
        setInspectionType(response.data.inspection_type || 0);
        setResponsibleName(response.data.vvm_responsible || '');
        setEmailResponsible(response.data.vvm_responsible_email || '');
        setPhoneResponsible(response.data.vvm_responsible_phone_number || '');
        if (response.data.latitude && response.data.longitude) {
          setPinPlace({
            lat: Number(response.data.latitude),
            lng: Number(response.data.longitude),
          });
        }
        if (response.data.requester_name) {
          setSolicitor(response.data.requester_name);
        }
        if (response.data.number) {
          setAddressNumber(response.data.number);
        } else {
          setIsWithoutNumber(true);
        }
        const associatedList = await getAssociatedListCallback(osId);
        setAssociatedWorkData(associatedList);
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error, true));
        setErrorMessage(true);
        setOpenSnackbar(true);
      }
    } else {
      setNewProperty(true);
    }

    if (!showClientKind && bankCompanyData) {
      const clientKind =
        bankCompanyData?.bank_client_kind ?? ClientKind.OTHER_BANK;
      setClientType(clientKind);
    }
  }, [osId, iptuLink, registerLink, bankCompanyData, showClientKind]);

  useEffect(() => {
    getDataCallback();
  }, [getDataCallback, iptuLink, registerLink]);

  const handleFileUpload = async (
    e: ChangeEvent<HTMLInputElement>,
    fileType: string
  ): Promise<void> => {
    if (!e.target.files?.item(0) || !validateFiles(e.target.files[0].type)) {
      setSnackbarMessage('Formato incorreto, selecione uma imagem ou pdf');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const file = e.target.files[0];

    if (checkPathname('/edit')) {
      const formData = new FormData();
      if (fileType === 'register') {
        formData.append('real_estate_registry', file);
      }
      if (fileType === 'iptu') {
        formData.append('iptu_document', file);
      }

      try {
        const response = await editWorkOrder(osId, formData);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        setSnackbarMessage('Dados alterados com sucesso!');
        setErrorMessage(false);
        setOpenSnackbar(true);
        if (fileType === 'register') {
          setRegisterLink(file.name);
        }
        if (fileType === 'iptu') {
          setIptuLink(file.name);
        }
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
      }
      return;
    }

    if (fileType === 'register') {
      setRegisterFileName(file.name);
      setRegisterFile(file);
    }
    if (fileType === 'iptu') {
      setIptuFileName(file.name);
      setIptuFile(file);
    }
  };

  const verifyCep = async (cep: string): Promise<void> => {
    const addressData = await handleSearchCep(cep);
    if (addressData) {
      setCep(addressData.cep);
      setUf(ufAbbreviationToEnum[addressData.uf] ?? 0);
      setCity(addressData.city);
      setDistrict(addressData.district);
      setAddress(addressData.address);
      setCepError(false);
    } else {
      setCepError(true);
    }
    setIsAddressEdited(true);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (cepError) {
      setSnackbarMessage('Adicione um CEP válido.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    if (isAddressEdited) {
      setSnackbarMessage('Pesquise a localização no mapa antes de salvar.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const requiredSelectFields = [
      evaluationType,
      goal,
      propertyFunction,
      propertyUse,
      uf,
      propertyType,
      ...(isHiveOwner ? [inspectionType] : []),
      ...(showClientKind ? [clientType] : []),
      ...(!isHiveOwner ? [concept, registrationUf] : []),
    ];
    if (requiredSelectFields.some(isPlaceholderSelectOption)) {
      setSnackbarMessage('Os dados de seleção são obrigatórios.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const isParcelGroup = parcelGroup.has(propertyType);
    const isPrivateUnit = privateUnitsGroup.has(propertyType);

    if (isHiveOwner && !isParcelGroup && builtArea === 0) {
      const areaName = isPrivateUnit ? 'área privativa' : 'área construída';
      setSnackbarMessage(`É necessário preencher a ${areaName}`);
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const formData = new FormData();
    formData.append('street', address);
    formData.append('zip_code', cep);
    formData.append('city', city);
    formData.append('client_name', client);
    formData.append('client_kind', clientType.toString());
    formData.append('address_complement', complement);
    formData.append('district', district);
    formData.append('evaluation_type', evaluationType.toString());
    formData.append('report_goal', goal.toString());
    formData.append('report_function', propertyFunction.toString());
    formData.append('use', propertyUse.toString());
    formData.append('requester_name', solicitor);
    formData.append('requires_inspection', requiresInspection.toString());
    formData.append('state', ufAbbreviation[uf]);
    formData.append('latitude', pinPlace.lat.toString());
    formData.append('longitude', pinPlace.lng.toString());
    formData.append('real_estate_kind', propertyType.toString());

    formData.append('number', isWithoutNumber ? 'null' : addressNumber);

    if (!isHiveOwner) {
      formData.append('communities_kind', concept.toString());
      formData.append('zone', zone.toString());
      formData.append('judicial_district', judicialDistrict);
      formData.append('uf', registrationUf.toString());
    }

    if (isHiveOwner) {
      formData.append('inspection_type', inspectionType.toString());
    }

    if (totalArea) {
      formData.append('total_area', totalArea.toString());
    }

    if (!isParcelGroup) {
      if (builtArea) formData.append('constructed_area', builtArea.toString());
    }

    if (registerFile) {
      formData.append('real_estate_registry', registerFile);
    }
    if (iptuFile) {
      formData.append('iptu_document', iptuFile);
    }

    if (newProperty || !hasAssociation) {
      formData.append('registry_office', registryOffice);
      formData.append('registration_number', registerNumber);
      formData.append('book', book);
    }

    if (isHiveOwner && inspectionType === InspectionType.REMOTE) {
      formData.append('vvm_responsible', responsibleName);
      formData.append('vvm_responsible_phone_number', phoneResponsible);
      formData.append('vvm_responsible_email', emailResponsible);
    }

    setSubmitLoading(true);

    if (checkPathname('/edit')) {
      try {
        const response = await editWorkOrder(osId, formData);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        const successMessage = 'Dados alterados com sucesso!';

        if (
          associatedWorkData.length === 0 &&
          shouldAssociate &&
          associateIds.length > 0
        ) {
          const props: AssociateProps = {
            data: {
              osId,
              bodyData: {
                work_order_ids: associateIds,
              },
            },
            successMessage,
            callback: navigateHome,
          };
          await handleWorkOrderAssociation(props);
        } else {
          setSnackbarMessage(successMessage);
          setErrorMessage(false);
          setOpenSnackbar(true);
          setSubmitLoading(false);
          navigateHome();
        }
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
        setSubmitLoading(false);
      }
    } else {
      try {
        const response = await createWorkOrder(formData);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (response.detail.status_code !== StatusCode.OK || !response.data) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        // eslint-disable-next-line max-len
        const successMessage = `Imóvel cadastrado com sucesso! Número de OS ${response.data.reference_number}`;

        if (shouldAssociate && associateIds.length > 0) {
          const props: AssociateProps = {
            data: {
              osId: response.data.id,
              bodyData: {
                work_order_ids: associateIds,
              },
            },
            successMessage,
            callback: navigateHome,
          };
          await handleWorkOrderAssociation(props);
        } else {
          setSnackbarMessage(successMessage);
          setErrorMessage(false);
          setOpenSnackbar(true);
          navigateHome();
        }
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
        setSubmitLoading(false);
      }
    }
  };

  const handleRemoveRequest = useCallback(async () => {
    const requestId = propertyData?.hive_request?.id;
    if (!requestId) {
      setSnackbarMessage('Algo deu errado, tente novamente.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    setRemoveRequestLoading(true);

    try {
      const response = await removeRequest(requestId);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }
      setSnackbarMessage('Atribuição removida com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      navigateHome();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setRemoveRequestLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyData]);

  useEffect(() => {
    const requiredFields = [
      client,
      clientType,
      solicitor,
      goal,
      propertyFunction,
      propertyUse,
      ...(isHiveOwner ? [inspectionType] : []),
    ];
    if (requiredFields.some(isPlaceholderSelectOption)) {
      setCompletedSections((prev) => ({ ...prev, reportData: false }));
    } else {
      setCompletedSections((prev) => ({ ...prev, reportData: true }));
    }
  }, [
    client,
    clientType,
    solicitor,
    goal,
    propertyFunction,
    propertyUse,
    propertyData,
    inspectionType,
    isHiveOwner,
  ]);

  useEffect(() => {
    const requiredFields = [
      propertyType,
      ...(!isHiveOwner
        ? [
            registerNumber,
            registryOffice,
            book,
            propertyType,
            concept,
            zone,
            judicialDistrict,
            registrationUf,
          ]
        : []),
    ];
    if (requiredFields.some(isPlaceholderSelectOption)) {
      setCompletedSections((prev) => ({ ...prev, propertyData: false }));
    } else {
      setCompletedSections((prev) => ({ ...prev, propertyData: true }));
    }
  }, [
    registerNumber,
    propertyType,
    concept,
    zone,
    judicialDistrict,
    registrationUf,
    propertyType,
    registryOffice,
    book,
  ]);

  useEffect(() => {
    const requiredFields = [cep, address, district, city, uf, !isAddressEdited];
    if (!isWithoutNumber) requiredFields.push(addressNumber);
    if (requiredFields.some(isPlaceholderSelectOption)) {
      setCompletedSections((prev) => ({ ...prev, propertyAddress: false }));
    } else {
      setCompletedSections((prev) => ({ ...prev, propertyAddress: true }));
    }
  }, [cep, address, addressNumber, district, city, uf, isAddressEdited]);

  useEffect(() => {
    const requiredFields = [builtArea];
    if (requiredFields.some(isPlaceholderSelectOption)) {
      setCompletedSections((prev) => ({ ...prev, propertyDetails: false }));
    } else {
      setCompletedSections((prev) => ({ ...prev, propertyDetails: true }));
    }
  }, [isHiveOwner, builtArea]);

  useEffect(() => {
    const requiredFields =
      inspectionType === InspectionType.REMOTE
        ? [responsibleName, phoneResponsible]
        : [];
    if (requiredFields.some(isPlaceholderSelectOption)) {
      setCompletedSections((prev) => ({
        ...prev,
        scheduleResponsibleData: false,
      }));
    } else {
      setCompletedSections((prev) => ({
        ...prev,
        scheduleResponsibleData: true,
      }));
    }
  }, [inspectionType, responsibleName, phoneResponsible]);

  return {
    handleSubmit,
    verifyCep,
    handleFileUpload,
    handleRemoveRequest,
    address,
    setAddress,
    addressNumber,
    setAddressNumber,
    isWithoutNumber,
    setIsWithoutNumber,
    builtArea,
    setBuiltArea,
    cep,
    setCep,
    cepError,
    city,
    setCity,
    client,
    setClient,
    clientType,
    setClientType,
    complement,
    setComplement,
    concept,
    setConcept,
    district,
    setDistrict,
    evaluationType,
    setEvaluationType,
    goal,
    setGoal,
    judicialDistrict,
    setJudicialDistrict,
    propertyFunction,
    setPropertyFunction,
    propertyType,
    setPropertyType,
    propertyUse,
    setPropertyUse,
    registerNumber,
    setRegisterNumber,
    registryOffice,
    setRegistryOffice,
    book,
    setBook,
    registrationUf,
    setRegistrationUf,
    setRequiresInspection,
    solicitor,
    setSolicitor,
    totalArea,
    setTotalArea,
    uf,
    setUf,
    zone,
    setZone,
    propertyData,
    registerFileName,
    iptuFileName,
    createdAt,
    downloadIptu,
    downloadRegister,
    referenceNumber,
    newProperty,
    searchMap,
    setSearchMap,
    isAddressEdited,
    setIsAddressEdited,
    hasSearched,
    setHasSearched,
    pinPlace,
    setPinPlace,
    submitLoading,
    removeRequestLoading,
    inspectionType,
    setInspectionType,
    responsibleName,
    setResponsibleName,
    emailResponsible,
    setEmailResponsible,
    phoneResponsible,
    setPhoneResponsible,
    completedSections,
    shouldAssociate,
    setShouldAssociate,
    setAssociateIds,
    associatedWorkData,
  };
};

export default useEntrance;
